'use client'

import { api, authApi, publicApi, userApi } from '@/shared/api/api'
import { AUTH_API_PATHS, AUTH_API_URLS } from '@/shared/constants/authApiConstants'
import { cognitoConfig } from '@/shared/constants/authEnvConstants'
import { AUTH_URLS } from '@/shared/constants/authUrlConstants'
import type {
  AuthTokens,
  LoginData,
  LoginResponse,
  SignupData,
  SignupResponse,
  User,
  UserRole,
  VerifyOtpApiResponse,
  VerifyOtpData,
} from '@/shared/types/auth.types'
import {
  configureAmplify,
  isAmplifyReady,
  refreshTokensWithAmplify,
} from '@/shared/utils/authAmplify'
import {
  clearStoredTokens,
  clearStoredUser,
  consumePostLoginRedirect,
  getSafeRedirectPath,
  getStoredTokens,
  getStoredUser,
  setPostLoginRedirect,
  setStoredTokens,
  setStoredUser,
} from '@/shared/utils/authSession'
import {
  decodeToken,
  getTimeUntilExpiration,
  getTokenExpirationTime,
  getUserFromTokens,
  isTokenExpired,
  isValidToken,
  isWhatsAppUser,
} from '@/shared/utils/authToken'
import { syncCartAfterLogin } from '@/store/_utils/cartSync'
import { syncWishlistAfterLogin } from '@/store/_utils/wishlistSync'

type CognitoTokenResponse = {
  access_token?: string
  id_token?: string
  refresh_token?: string
  accessToken?: string
  idToken?: string
  refreshToken?: string
}

type CurrentUserResponse = User

type PhoneRefreshResponse = {
  accessToken?: string
  idToken?: string
  refreshToken?: string
  access_token?: string
  id_token?: string
  refresh_token?: string
}

class SharedAuthServices {
  private isHandlingRefreshFailure = false

  private normalizeRole(
    role: string | null | undefined,
    fallback: UserRole = 'buyer',
  ): UserRole {
    return role === 'buyer' || role === 'vendor' || role === 'admin' ? role : fallback
  }

  private getSearchParam(
    searchParams: URLSearchParams,
    ...keys: string[]
  ): string | null {
    for (const key of keys) {
      const value = searchParams.get(key)?.trim()
      if (value) {
        return value
      }
    }

    return null
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (this.isRecord(error)) {
      const message = error.message
      if (typeof message === 'string' && message.trim() !== '') {
        return message
      }

      const errorMessage = error.error
      if (typeof errorMessage === 'string' && errorMessage.trim() !== '') {
        return errorMessage
      }
    }

    if (error instanceof Error && error.message.trim() !== '') {
      return error.message
    }

    return fallback
  }

  private toAuthTokens(
    data: CognitoTokenResponse | null | undefined,
    fallbackRefreshToken: string | null | undefined,
    errorMessage: string,
  ): AuthTokens {
    const accessToken = data?.access_token ?? data?.accessToken
    const idToken = data?.id_token ?? data?.idToken
    const refreshToken = data?.refresh_token ?? data?.refreshToken ?? fallbackRefreshToken

    if (!isValidToken(accessToken)) {
      throw new Error(errorMessage)
    }

    return {
      accessToken,
      ...(isValidToken(idToken) && { idToken }),
      ...(isValidToken(refreshToken) && { refreshToken }),
    }
  }

  private getOtpTokens(response: VerifyOtpApiResponse): AuthTokens {
    const accessToken = response.token

    if (!isValidToken(accessToken)) {
      clearStoredTokens()
      throw new Error('Invalid OTP')
    }

    return { accessToken }
  }

  private async updateAuthStore(
    user: User | null,
    tokens: AuthTokens | null,
  ): Promise<void> {
    const { useAuthStore } = await import('@/store/authStore')

    useAuthStore.setState({
      user,
      accessToken: tokens?.accessToken ?? null,
      isAuthenticated: !!tokens,
      isLoading: false,
    })
  }

  private dispatchUserLogin(user: User | null, tokens: AuthTokens): void {
    if (typeof window === 'undefined') {
      return
    }

    window.dispatchEvent(new CustomEvent('userLogin', { detail: { user, tokens } }))
  }

  private dispatchTokensUpdated(user: User | null, tokens: AuthTokens): void {
    if (typeof window === 'undefined') {
      return
    }

    window.dispatchEvent(
      new CustomEvent('authTokensUpdated', { detail: { user, tokens } }),
    )
  }

  private clearLocalSession(): void {
    clearStoredTokens()
    clearStoredUser()

    if (typeof window !== 'undefined') {
      localStorage.removeItem('exaltride_guest_cart')
      localStorage.removeItem('exaltride_product_details_cache')
    }
  }

  getSafeRedirectPath(candidate: string | null | undefined, fallback = '/'): string {
    return getSafeRedirectPath(candidate, fallback)
  }

  setPostLoginRedirect(path: string | null | undefined): void {
    setPostLoginRedirect(path)
  }

  consumePostLoginRedirect(fallback = '/'): string {
    return consumePostLoginRedirect(fallback)
  }

  getTokens(): AuthTokens | null {
    return getStoredTokens()
  }

  setTokens(tokens: AuthTokens): void {
    setStoredTokens(tokens)
    const user = this.getUser() ?? this.getUserFromToken()
    void this.updateAuthStore(user, tokens)
    this.dispatchTokensUpdated(user, tokens)
  }

  clearTokens(): void {
    clearStoredTokens()
    void this.updateAuthStore(this.getUser(), null)
  }

  getUser(): User | null {
    return getStoredUser()
  }

  setUser(user: User): void {
    setStoredUser(user)
  }

  clearUser(): void {
    clearStoredUser()
  }

  decodeToken(token: string): Record<string, unknown> | null {
    return decodeToken(token)
  }

  getUserFromToken(): User | null {
    return getUserFromTokens(this.getTokens())
  }

  isAuthenticated(): boolean {
    return this.getTokens() !== null
  }

  isPhoneAuthenticated(tokens: AuthTokens | null = this.getTokens()): boolean {
    return isWhatsAppUser(tokens)
  }

  getTokenExpirationTime(): number | null {
    return getTokenExpirationTime(this.getTokens())
  }

  isTokenExpired(bufferMs = 0): boolean {
    return isTokenExpired(this.getTokens(), bufferMs)
  }

  getTimeUntilExpiration(): number | null {
    return getTimeUntilExpiration(this.getTokens())
  }

  async signup(data: SignupData): Promise<SignupResponse> {
    try {
      return await publicApi.post<SignupResponse>(AUTH_API_URLS.signup, data, {
        headers: { 'Content-Type': 'application/json' },
      })
    } catch (error) {
      console.error('Signup error:', error)
      throw new Error(this.getErrorMessage(error, 'Signup failed'))
    }
  }

  async login(data: LoginData): Promise<LoginResponse> {
    return api.post<LoginResponse>(AUTH_API_PATHS.sessionLogin, data)
  }

  async requestOtp(phoneNumber: string): Promise<LoginResponse> {
    return authApi.post<LoginResponse>(AUTH_API_PATHS.requestOtp, { phoneNumber })
  }

  async verifyOtp(data: VerifyOtpData): Promise<VerifyOtpApiResponse> {
    return authApi.post<VerifyOtpApiResponse>(AUTH_API_PATHS.verifyOtp, data)
  }

  async completeLogin(
    tokens: AuthTokens,
    user: User | null = getUserFromTokens(tokens),
  ): Promise<{ tokens: AuthTokens; user: User | null }> {
    setStoredTokens(tokens)

    if (user) {
      setStoredUser(user)
    } else {
      clearStoredUser()
    }

    await this.updateAuthStore(user, tokens)

    try {
      await syncCartAfterLogin()
    } catch (error) {
      console.error('Failed to merge cart on login:', error)
    }

    try {
      await syncWishlistAfterLogin()
    } catch (error) {
      console.error('Failed to merge wishlist on login:', error)
    }

    this.dispatchUserLogin(user, tokens)
    return { tokens, user }
  }

  async completeOtpLogin(
    response: VerifyOtpApiResponse,
  ): Promise<{ tokens: AuthTokens; user: User | null }> {
    const tokens = this.getOtpTokens(response)
    const fallbackUser = response.user ?? getUserFromTokens(tokens)

    setStoredTokens(tokens)

    try {
      const currentUser = await userApi.get<CurrentUserResponse>('/me', {
        headers: {
          Accept: 'application/json',
        },
      })

      return this.completeLogin(tokens, currentUser)
    } catch (error) {
      console.warn('Failed to fetch current user profile after OTP verification:', error)
      return this.completeLogin(tokens, fallbackUser)
    }
  }

  initiateGoogleSSO(role: UserRole = 'buyer', redirectPath?: string | null): void {
    if (typeof window === 'undefined') {
      return
    }
    setPostLoginRedirect(redirectPath)

    const params = new URLSearchParams({
      role: this.normalizeRole(role),
    })

    const googleSsoUrl = `${AUTH_URLS.googleSSOStart}?${params.toString()}`
    // Browser follows redirects — you won't see an HTTP body here; inspect the callback logs after login.
    console.log('[Google SSO] Navigating to auth /google:', googleSsoUrl)

    window.location.assign(googleSsoUrl)
  }

  async completeGoogleCallbackLogin(searchParams: URLSearchParams): Promise<{
    tokens: AuthTokens
    user: User | null
  }> {
    const oauthError = this.getSearchParam(searchParams, 'error_description', 'error')
    if (oauthError) {
      throw new Error(oauthError)
    }

    const token = this.getSearchParam(searchParams, 'token')
    const callbackMessage = this.getSearchParam(searchParams, 'message')

    if (!isValidToken(token)) {
      throw new Error(
        callbackMessage ?? 'Authentication response is missing token',
      )
    }

    const tokens: AuthTokens = { accessToken: token }
    const tokenUser = getUserFromTokens(tokens)
    const user: User = {
      id: this.getSearchParam(searchParams, 'user_id', 'userId') ?? tokenUser?.id,
      phoneNumber: tokenUser?.phoneNumber ?? '',
      name: this.getSearchParam(searchParams, 'name') ?? tokenUser?.name ?? '',
      role: this.normalizeRole(
        this.getSearchParam(searchParams, 'role') ?? tokenUser?.role ?? null,
      ),
      email: this.getSearchParam(searchParams, 'email') ?? tokenUser?.email ?? undefined,
    }

    return this.completeLogin(tokens, user)
  }

  private getSsoRefreshUserId(tokens: AuthTokens): string | null {
    const fromStored = this.getUser()?.cognitoSub?.trim()
    if (isValidToken(fromStored)) {
      return fromStored
    }

    const fromTokenProfile = getUserFromTokens(tokens)?.cognitoSub?.trim()
    if (isValidToken(fromTokenProfile)) {
      return fromTokenProfile
    }

    const tokenToDecode = tokens.idToken ?? tokens.accessToken
    const decoded = decodeToken(tokenToDecode)
    const sub = typeof decoded?.sub === 'string' ? decoded.sub.trim() : ''
    return isValidToken(sub) ? sub : null
  }

  async refreshSSOTokensViaCognito(tokens: AuthTokens): Promise<AuthTokens | null> {
    const cognitoSub = this.getSsoRefreshUserId(tokens)
    if (!cognitoSub) {
      console.error('SSO token refresh skipped: missing Cognito sub (userId)')
      return null
    }

    try {
      const response = await publicApi.post<CognitoTokenResponse>(
        AUTH_API_URLS.refreshToken,
        { userId: cognitoSub, refreshToken: tokens.refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      const newTokens = this.toAuthTokens(
        response,
        tokens.refreshToken,
        'SSO token refresh response missing required tokens',
      )

      this.setTokens(newTokens)
      return newTokens
    } catch (error) {
      console.error('SSO Cognito token refresh error:', error)
      return null
    }
  }

  async refreshWhatsAppTokensViaCognito(tokens: AuthTokens): Promise<AuthTokens | null> {
    try {
      const response = await publicApi.post<CognitoTokenResponse>(
        AUTH_API_URLS.refreshWhatsapp,
        { refreshToken: tokens.refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      const data = response

      if (!data.access_token || !data.id_token) {
        console.error('WhatsApp token refresh response missing required tokens')
        return null
      }

      const newTokens: AuthTokens = {
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: tokens.refreshToken,
      }

      this.setTokens(newTokens)
      return newTokens
    } catch (error) {
      console.error('WhatsApp Cognito token refresh error:', error)
      return null
    }
  }

  async refreshTokens(): Promise<AuthTokens | null> {
    const tokens = this.getTokens()
    if (!tokens?.refreshToken) {
      return null
    }

    if (!isWhatsAppUser(tokens) && (isAmplifyReady() || configureAmplify())) {
      try {
        const amplifyTokens = await refreshTokensWithAmplify()

        if (amplifyTokens) {
          const newTokens: AuthTokens = {
            accessToken: amplifyTokens.accessToken,
            idToken: amplifyTokens.idToken,
            refreshToken: tokens.refreshToken,
          }

          this.setTokens(newTokens)
          return newTokens
        }
      } catch (error) {
        console.error('Amplify refresh failed, falling back to Cognito refresh:', error)
      }
    }

    return this.isPhoneAuthenticated(tokens)
      ? this.refreshPhoneTokensViaApi(tokens)
      : this.refreshSSOTokensViaCognito(tokens)
  }

  private getPhoneRefreshUserId(tokens: AuthTokens): string | null {
    const storedUserId = this.getUser()?.phoneNumber?.trim()
    if (isValidToken(storedUserId)) {
      return storedUserId
    }

    const tokenUserId = this.getUserFromToken()?.phoneNumber?.trim()
    if (isValidToken(tokenUserId)) {
      return tokenUserId
    }

    const tokenToDecode = tokens.idToken ?? tokens.accessToken
    const decoded = decodeToken(tokenToDecode)
    const phoneNumber = decoded?.phone_number ?? decoded?.['custom:phone_number']
    return isValidToken(phoneNumber) ? phoneNumber : null
  }

  private async refreshPhoneTokensViaApi(tokens: AuthTokens): Promise<AuthTokens | null> {
    try {
      const userId = this.getPhoneRefreshUserId(tokens)
      if (!userId) {
        throw new Error('Phone refresh user id is missing')
      }

      const response = await publicApi.post<PhoneRefreshResponse>(
        AUTH_API_URLS.phoneRefreshToken,
        {
          userId,
          refreshToken: tokens.refreshToken,
        },
        {
          headers: { 'Content-Type': 'application/json' },
        },
      )

      const newTokens = this.toAuthTokens(
        response,
        tokens.refreshToken,
        'Phone refresh response missing required tokens',
      )

      this.setTokens(newTokens)
      return newTokens
    } catch (error) {
      console.error('Phone token refresh error:', error)
      return null
    }
  }

  async handleRefreshFailure(): Promise<void> {
    if (this.isHandlingRefreshFailure) {
      return
    }

    this.isHandlingRefreshFailure = true

    try {
      this.clearLocalSession()
      await this.updateAuthStore(null, null)

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('userLogout'))
      }
    } finally {
      this.isHandlingRefreshFailure = false
    }
  }

  async logout(): Promise<void> {
    const tokens = this.getTokens()
    const user = this.getUserFromToken()

    let role: 'buyer' | 'whatsapp' = 'buyer'
    if (user?.phoneNumber && user.phoneNumber.startsWith('+')) {
      role = 'whatsapp'
    }

    if (tokens?.accessToken) {
      try {
        await publicApi.post(
          AUTH_API_URLS.logout,
          {
            accessToken: tokens.accessToken,
            ...(tokens.refreshToken && { refreshToken: tokens.refreshToken }),
            role,
          },
          { headers: { 'Content-Type': 'application/json', skipToast: 'true' } },
        )
      } catch (error) {
        console.warn('Backend logout API call failed:', error)
      }
    }

    this.clearLocalSession()
  }

  logoutWithCognito(): void {
    if (typeof window === 'undefined') {
      return
    }

    this.clearLocalSession()

    const logoutUrl = new URL(AUTH_URLS.cognitoLogout)
    logoutUrl.searchParams.set('client_id', cognitoConfig.clientId ?? '')
    logoutUrl.searchParams.set('logout_uri', AUTH_URLS.postLogoutRedirect)

    window.location.href = logoutUrl.toString()
  }
}

export const authServices = new SharedAuthServices()
